import { useLayoutEffect, useState } from 'react';

/**
 * Reads the one perspective ratio the whole app shares: `--orbit-tilt` in
 * web/src/design/tokens.css.
 *
 * Every ellipse in Loggy — the orbit rings, the body scan's cross-sections,
 * the atmosphere's glow and pings — is a horizontal circle seen at the same
 * angle, so its `ry` is always `rx` times this ratio. Keeping the number in
 * exactly one place is what stops the 3D scene and the 2D fallbacks from
 * drifting apart until they stop looking like the same space (see CLAUDE.md).
 *
 * Geometry computed in JavaScript cannot read a CSS variable through a
 * stylesheet, so it has to be read off the document — but it must still be
 * read, never copied. This hook is the single place that read happens.
 *
 * It is deliberately forgiving. The figure and the atmosphere are decoration;
 * a dashboard that will not render because a custom property came back as an
 * empty string is a far worse failure than one drawn without perspective.
 */

/**
 * Used only when the token cannot be read at all. It is a neutral "no
 * perspective" value, NOT a second copy of the real ratio — copying that
 * number here is precisely what this hook exists to prevent. If it is ever
 * visible, the design tokens failed to load and the scene has bigger
 * problems than a flat plane.
 */
const NO_TILT = 1;

function readOrbitTilt(): number | null {
  if (typeof document === 'undefined') return null;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--orbit-tilt');
  const value = Number.parseFloat(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * @param override Skips the document read entirely. For a render path with
 *   no computed style to consult — a non-browser test, or a caller that has
 *   already resolved the tilt and wants every layer to agree on one value.
 */
export function useOrbitTilt(override?: number): number {
  // The token is loaded by main.tsx's `import './design/tokens.css'` before
  // anything renders, so the first read normally already succeeds and no
  // frame is ever painted untilted. The layout effect is the safety net for
  // the case where it does not (a stylesheet still in flight), and runs
  // before paint, so a corrected value never flashes either.
  const [tilt, setTilt] = useState(() => override ?? readOrbitTilt() ?? NO_TILT);

  useLayoutEffect(() => {
    if (override !== undefined) return;
    const value = readOrbitTilt();
    if (value !== null) setTilt(value);
  }, [override]);

  return override ?? tilt;
}
